import { useCallback, useEffect, useState } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useToast } from "@/components/ui/use-toast"
import { cn } from "@/lib/utils"
import { CalendarPlus, Inbox, Loader2, RefreshCw, UserCheck, UserX, X, XCircle } from "lucide-react"
import { reenrollPatient } from "@/features/apcm-ops/api/apcm-ops.api"
import * as outreachApi from "../api/outreach.api"
import { BookCallDialog, type BookCallChoice } from "./BookCallDialog"
import { describePreference, suggestedTime } from "../lib/preference"
import { formatInTz } from "../lib/time"
import { SOURCE_LABEL, inboxActions, inboxGuidance, inboxKind, inboxLabel } from "../lib/inbox"
import type { InboxAction, InboxItem, InboxMessage, InboxSource, InboxStatus } from "../types/outreach.types"

const msgText = (m: InboxMessage) => m.content ?? ""
const msgTime = (m: InboxMessage) => m.timestamp ?? null
const isInbound = (m: InboxMessage) => m.direction === "inbound"
const isSystem = (m: InboxMessage) => m.direction === "system"
const itemId = (i: InboxItem) => i.id

function rawWords(item: InboxItem): string | null {
  if (item.raw_words) return item.raw_words
  if (item.preference?.raw_text) return item.preference.raw_text
  const lastIn = [...(item.thread || [])].reverse().find(isInbound)
  return lastIn ? msgText(lastIn) : null
}

const hasPatientTime = (item: InboxItem | null) => !!item?.preference?.windows?.length

function itemHeading(item: InboxItem): string {
  if (inboxKind(item) === "shared_number") return "Several patients share this number"
  if (!item.patient_id) return "No patient linked"
  return item.patient_name || "Unknown patient"
}

const isOpen = (i: InboxItem) => !i.status || i.status.toLowerCase() === "pending"

interface ConfirmState {
  item: InboxItem
  action: "decline" | "not_patient" | "reenroll"
}

interface ReplyInboxProps {
  orgId: string
  onCountChange?: (n: number) => void
  /** Only items of this source (e.g. outreach_not_my_patient, from the weekly report link). */
  source?: InboxSource | null
  /** pending (default), completed or all. */
  status?: InboxStatus
}

/** GET /outreach/org/{org}/inbox + POST /outreach/inbox/{id}/resolve */
export function ReplyInbox({ orgId, onCountChange, source = null, status = "pending" }: ReplyInboxProps) {
  const { toast } = useToast()
  const [items, setItems] = useState<InboxItem[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [forbidden, setForbidden] = useState(false)
  const [loading, setLoading] = useState(false)
  const [working, setWorking] = useState<string | null>(null)
  const [booking, setBooking] = useState<InboxItem | null>(null)
  const [confirm, setConfirm] = useState<ConfirmState | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    setForbidden(false)
    try {
      const data = await outreachApi.getInbox(orgId, { status, source })
      setItems(data || [])
      onCountChange?.((data || []).length)
    } catch (err) {
      setForbidden(outreachApi.errorStatus(err) === 403)
      setError(outreachApi.errorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [orgId, onCountChange, status, source])

  useEffect(() => {
    load()
  }, [load])

  const resolve = async (item: InboxItem, action: InboxAction, choice?: BookCallChoice) => {
    const id = itemId(item)
    const at = choice?.at
    setWorking(id)
    try {
      await outreachApi.resolveInboxItem(id, {
        action,
        ...(at ? { at } : {}),
        ...(choice?.usePreference ? { use_preference: true } : {}),
      })
      const name = item.patient_name || "Patient"
      const msg: Record<InboxAction, string> = {
        book: `Call booked for ${name}${at ? ` on ${formatInTz(at)}` : " at their requested time"}`,
        decline: `${name} recorded as declined`,
        not_patient: `${name} marked as not the clinic's patient`,
        dismiss: "Dismissed",
      }
      toast({ title: msg[action] })
      setItems((prev) => {
        const next = (prev || []).filter((i) => itemId(i) !== id)
        onCountChange?.(next.length)
        return next
      })
    } catch (err) {
      toast({ title: "Action failed", description: outreachApi.errorMessage(err), variant: "destructive" })
      throw err
    } finally {
      setWorking(null)
    }
  }

  /** Undo a "not my practice" disenrollment, then close the item. */
  const reenroll = async (item: InboxItem) => {
    if (!item.patient_id) return
    const id = itemId(item)
    const name = item.patient_name || "Patient"
    setWorking(id)
    try {
      await reenrollPatient(item.patient_id, {
        organization_id: orgId,
        note: `Re-enrolled from the outreach inbox: "not my practice" was a mistake (item ${id})`,
      })
    } catch (err) {
      toast({ title: "Re-enroll failed", description: outreachApi.errorMessage(err), variant: "destructive" })
      setWorking(null)
      throw err
    }
    try {
      await outreachApi.resolveInboxItem(id, { action: "dismiss" })
      toast({ title: `${name} re-enrolled`, description: "They go back to needing consent." })
      setItems((prev) => {
        const next = (prev || []).filter((i) => itemId(i) !== id)
        onCountChange?.(next.length)
        return next
      })
    } catch (err) {
      toast({
        title: `${name} re-enrolled, but the item is still open`,
        description: outreachApi.errorMessage(err),
        variant: "destructive",
      })
      load()
    } finally {
      setWorking(null)
    }
  }

  if (forbidden) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-destructive">
          You don't have access to this organization's inbox. Ask your administrator for access, or sign in with an
          account that belongs to this clinic.
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex items-center justify-between gap-2 py-6 text-sm">
          <span className="text-destructive">Could not load the inbox: {error}</span>
          <Button size="sm" variant="outline" onClick={load}>
            Retry
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (!items) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {source
            ? `${SOURCE_LABEL[source] || source}${status === "all" ? ", open and closed" : status === "completed" ? ", closed" : ""}.`
            : "Patient replies and outreach problems for the whole clinic that need a person."}
        </p>
        <Button size="sm" variant="ghost" onClick={load} disabled={loading} className="gap-1.5">
          <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} /> Refresh
        </Button>
      </div>

      {items.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            <Inbox className="mx-auto mb-2 h-8 w-8" />
            {source || status !== "pending"
              ? "Nothing here."
              : "Nothing waiting. Replies the text bot could not handle will show up here."}
          </CardContent>
        </Card>
      ) : (
        items.map((item) => {
          const thread = (item.thread || []).slice(-10)
          const words = rawWords(item)
          const acts = inboxActions(item)
          const open = isOpen(item)
          const guidance = inboxGuidance(item)
          const stuck = inboxKind(item) === "stuck"
          const busy = working === itemId(item)
          return (
            <Card key={itemId(item)} className="min-w-0">
              <CardContent className="space-y-3 p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium break-words">{itemHeading(item)}</p>
                    <p className="text-sm text-muted-foreground break-words">{item.title}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!open && <Badge variant="secondary">Done</Badge>}
                    <Badge
                      variant="outline"
                      className={cn(
                        (stuck || item.source === "sms_unprocessed") && "border-destructive/40 text-destructive",
                      )}
                    >
                      {inboxLabel(item)}
                    </Badge>
                    {item.created_at && <span className="text-xs text-muted-foreground">{formatInTz(item.created_at)}</span>}
                  </div>
                </div>

                {guidance && open && <p className="rounded-md bg-muted/40 px-3 py-2 text-sm">{guidance}</p>}
                {item.description && <p className="text-sm break-words">{item.description}</p>}

                {thread.length > 0 && (
                  <div className="max-h-56 space-y-1.5 overflow-y-auto rounded-lg bg-muted/30 p-3">
                    {thread.map((m, i) =>
                      isSystem(m) ? (
                        <p key={i} className="text-center text-[11px] text-muted-foreground">
                          {msgText(m)}
                          {msgTime(m) ? ` · ${formatInTz(msgTime(m))}` : ""}
                        </p>
                      ) : (
                      <div key={i} className={cn("flex", isInbound(m) ? "justify-start" : "justify-end")}>
                        <div
                          className={cn(
                            "max-w-[85%] rounded-lg px-3 py-1.5 text-sm whitespace-pre-wrap break-words",
                            isInbound(m) ? "bg-card border" : "bg-primary/10",
                          )}
                        >
                          {msgText(m)}
                          {msgTime(m) && (
                            <span className="mt-0.5 block text-[10px] text-muted-foreground">
                              {isInbound(m) ? "Patient" : "Pear"} · {formatInTz(msgTime(m))}
                            </span>
                          )}
                        </div>
                      </div>
                      ),
                    )}
                  </div>
                )}

                {item.patient_id && (
                <div className="grid gap-2 text-sm sm:grid-cols-2">
                  <div className="rounded-md border p-2">
                    <p className="text-xs text-muted-foreground">What we understood</p>
                    <p>{describePreference(item.preference)}</p>
                  </div>
                  <div className="rounded-md border p-2">
                    <p className="text-xs text-muted-foreground">Patient's own words</p>
                    <p className="italic break-words">{words ? `"${words}"` : "-"}</p>
                  </div>
                </div>
                )}

                {item.pending_call?.schedule_timestamp && (
                  <p className="text-xs text-muted-foreground">
                    Call already booked for {formatInTz(item.pending_call.schedule_timestamp)}
                    {item.pending_call.requested_by === "PATIENT" ? " (patient's time)" : ""}. Booking moves it.
                  </p>
                )}

                {acts.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {acts.includes("reenroll") && (
                    <Button size="sm" variant="outline" onClick={() => setConfirm({ item, action: "reenroll" })} disabled={busy} className="gap-1.5">
                      <UserCheck className="h-3.5 w-3.5" /> Re-enroll (mistake)
                    </Button>
                  )}
                  {acts.includes("book") && (
                    <Button size="sm" onClick={() => setBooking(item)} disabled={busy} className="gap-1.5">
                      <CalendarPlus className="h-3.5 w-3.5" /> Book at time…
                    </Button>
                  )}
                  {acts.includes("decline") && (
                    <Button size="sm" variant="outline" onClick={() => setConfirm({ item, action: "decline" })} disabled={busy} className="gap-1.5">
                      <XCircle className="h-3.5 w-3.5" /> Decline
                    </Button>
                  )}
                  {acts.includes("not_patient") && (
                    <Button size="sm" variant="outline" onClick={() => setConfirm({ item, action: "not_patient" })} disabled={busy} className="gap-1.5">
                      <UserX className="h-3.5 w-3.5" /> Not our patient
                    </Button>
                  )}
                  {acts.includes("dismiss") && (
                  <Button size="sm" variant="ghost" onClick={() => resolve(item, "dismiss").catch(() => {})} disabled={busy} className="gap-1.5">
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />} Dismiss
                  </Button>
                  )}
                </div>
                )}
              </CardContent>
            </Card>
          )
        })
      )}

      <BookCallDialog
        open={!!booking}
        onOpenChange={(o) => !o && setBooking(null)}
        patientName={booking?.patient_name || "the patient"}
        initialDate={booking?.preference?.not_before || undefined}
        initialTime={suggestedTime(booking?.preference)}
        pendingCallAt={booking?.pending_call?.schedule_timestamp}
        preferenceSummary={hasPatientTime(booking) ? describePreference(booking?.preference) : null}
        onConfirm={async (choice) => {
          if (booking) await resolve(booking, "book", choice)
        }}
      />

      <Dialog open={!!confirm} onOpenChange={(o) => !o && !working && setConfirm(null)}>
        <DialogContent>
          {confirm && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {confirm.action === "decline"
                    ? `Record that ${confirm.item.patient_name || "this patient"} declined?`
                    : confirm.action === "reenroll"
                      ? `Re-enroll ${confirm.item.patient_name || "this patient"}?`
                      : `Mark ${confirm.item.patient_name || "this patient"} as not the clinic's patient?`}
                </DialogTitle>
                <DialogDescription asChild>
                  <div className="space-y-2 text-sm">
                    {confirm.action === "reenroll" ? (
                      <>
                        <p>
                          Use this only if they are the clinic's patient after all. They go back to needing consent
                          and the clinic program can reach them again.
                        </p>
                        <p>Calls and texts that were cancelled are not re-booked automatically.</p>
                        <p className="font-medium text-foreground">No text is sent to the patient about this.</p>
                      </>
                    ) : confirm.action === "decline" ? (
                      <>
                        <p>Their APCM consent is recorded as declined, entered by staff.</p>
                        <p>
                          Every call and text still scheduled for them is cancelled. They get no more consent
                          calls or texts from this campaign.
                        </p>
                        <p className="font-medium text-foreground">No text is sent to the patient about this.</p>
                      </>
                    ) : (
                      <>
                        <p>They are taken out of APCM (disenrolled) with the reason "not the clinic's patient".</p>
                        <p>Every call and text still scheduled for them stops. The clinic can re-enroll them later.</p>
                        <p className="font-medium text-foreground">No text is sent to the patient about this.</p>
                      </>
                    )}
                  </div>
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setConfirm(null)} disabled={!!working}>
                  Cancel
                </Button>
                <Button
                  variant={confirm.action === "reenroll" ? "default" : "destructive"}
                  disabled={!!working}
                  onClick={async () => {
                    try {
                      if (confirm.action === "reenroll") await reenroll(confirm.item)
                      else await resolve(confirm.item, confirm.action)
                      setConfirm(null)
                    } catch {
                      /* toast already shown */
                    }
                  }}
                >
                  {working ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : confirm.action === "decline" ? (
                    "Record decline"
                  ) : confirm.action === "reenroll" ? (
                    "Re-enroll"
                  ) : (
                    "Not our patient"
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
